import { useEffect, useMemo, useState } from 'react';
import { IonContent, IonPage } from '@ionic/react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { ArrowLeft, MessageCircle, Phone, Share2, WifiOff } from 'lucide-react';
import { Avatar, Badge, Button, Card, Spinner } from '@tcrd/ui';
import { useToast } from '../../components/toast';
import { api, errorMessage } from '../../lib/api';
import { shareLink } from '../../lib/share';
import { MapView } from '../../components/map/MapView';
import type { MapEngine, MarkerSpec } from '../../components/map/engine';
import { fmtDuration, fmtKm, fmtTime } from '../../lib/format';
import { useRide } from '../common/RideDetailPage';
import { useLiveRide } from '../common/useLiveRide';
import { routeMarkers } from './RequestDetailPage';
import { SafetyCenter } from '../safety/SafetyCenter';
import { announceRideCheck } from '../safety/RideCheckModal';
import { DestinationChangeCustomer } from '../safety/DestinationChange';

const HEADLINE: Record<string, string> = {
  CONFIRMED: 'Tu viaje está confirmado',
  DRIVER_ON_THE_WAY: 'Tu conductor va en camino',
  DRIVER_ARRIVED: 'Tu conductor llegó',
  IN_PROGRESS: 'En camino a tu destino',
  COMPLETED: 'Viaje finalizado',
  CANCELLED: 'Viaje cancelado',
};

function ago(ms: number) {
  const s = Math.round((Date.now() - ms) / 1000);
  return s < 60 ? `hace ${s} s` : `hace ${Math.round(s / 60)} min`;
}

/** Seguimiento del cliente: mapa protagonista con el vehículo animado. */
export function LiveRidePage() {
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const ride = useRide(id);
  const toast = useToast();
  const [sharing, setSharing] = useState(false);
  const shareTrip = async () => {
    setSharing(true);
    try {
      const { url } = await api<{ url: string }>(`/rides/${id}/share`, { method: 'POST' });
      const r = await shareLink(url, 'Sigue mi viaje', 'Sigue mi viaje en vivo con TuConductorRD:');
      if (r === 'copied') toast('Enlace copiado. Pégalo en WhatsApp o donde quieras.', 'success');
    } catch (e) {
      toast(errorMessage(e), 'danger');
    } finally {
      setSharing(false);
    }
  };
  const [engine, setEngine] = useState<MapEngine | null>(null);
  const live = useLiveRide(id, engine);
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);

  const r = ride.data;
  const nav = live.snapshot.data?.navigation;
  const markers = useMemo<MarkerSpec[]>(() => (r ? routeMarkers(r) : []), [r]);
  // "¿Todo bien?" pendiente (por ejemplo si el aviso llegó con la app cerrada).
  const checkId = r?.rideCheck?.id;
  useEffect(() => {
    if (checkId) announceRideCheck(id);
  }, [checkId, id]);
  useEffect(() => {
    if (r?.status === 'COMPLETED') {
      const t = setTimeout(() => history.replace(`/app/rides/${id}`), 2500);
      return () => clearTimeout(t);
    }
  }, [r?.status, history, id]);

  const etaClock = live.eta !== null ? fmtTime(new Date(Date.now() + live.eta * 1000)) : null;

  return (
    <IonPage>
      <IonContent fullscreen scrollY={false}>
        <div className="relative h-full">
          <MapView onEngine={setEngine} className="absolute inset-0" markers={markers} route={nav?.polyline ?? r?.route.polyline ?? null} fitKey={`${id}-${nav?.target ?? ''}`} padding={80} />
          <Link to={`/app/rides/${id}`} className="absolute left-4 top-[max(env(safe-area-inset-top),1rem)] tap size-11 rounded-full bg-[var(--surface)] shadow-[var(--shadow-float)] flex items-center justify-center" aria-label="Volver">
            <ArrowLeft className="size-5" />
          </Link>
          <div className="absolute right-4 top-[max(env(safe-area-inset-top),1rem)] flex items-center gap-2">
            {!live.connected && (
              <div className="rounded-full bg-black/75 text-white text-xs px-3 py-2 flex items-center gap-1.5">
                <WifiOff className="size-4" aria-hidden /> Reconectando…
              </div>
            )}
            {r && <SafetyCenter ride={r} role="customer" />}
          </div>

          <div className="absolute inset-x-0 bottom-0 p-3 pb-[max(env(safe-area-inset-bottom),0.75rem)]">
            {!r ? (
              <Card className="p-6"><Spinner /></Card>
            ) : (
              <Card className="p-5 shadow-[var(--shadow-float)] max-w-xl mx-auto max-h-[70vh] overflow-y-auto">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h1 className="text-xl font-bold">{HEADLINE[r.status] ?? r.status}</h1>
                    {live.snapshot.data?.live && live.eta !== null && (
                      <p className="text-sm text-[var(--text-muted)]">
                        {r.status === 'IN_PROGRESS' ? 'Llegada' : 'Llega'} en <strong className="text-[var(--text)]">{fmtDuration(live.eta)}</strong>
                        {etaClock ? ` · ${etaClock}` : ''}
                        {live.remaining !== null ? ` · ${fmtKm(live.remaining)}` : ''}
                      </p>
                    )}
                  </div>
                  {live.stale ? <Badge tone="warning">Sin señal</Badge> : live.snapshot.data?.live ? <Badge tone="success">En vivo</Badge> : null}
                </div>
                {live.stale && live.lastAt && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">Mostrando la última ubicación conocida ({ago(live.lastAt)}). Se actualizará cuando el conductor recupere la señal.</p>
                )}
                {!live.stale && live.lastAt && <p className="mt-1 text-xs text-[var(--text-muted)]">Actualizado {ago(live.lastAt)}</p>}

                {r.pin?.code && !r.pin.verified && (
                  <div className="mt-3 rounded-2xl bg-[var(--surface-2)] px-4 py-3 flex items-center justify-between gap-3">
                    <p className="text-sm">Dile este PIN al conductor al subir</p>
                    <span className="font-mono text-2xl font-extrabold tracking-[0.3em]">{r.pin.code}</span>
                  </div>
                )}
                <div className="mt-4 flex items-center gap-3">
                  <Avatar src={r.driver.photoUrl} firstName={r.driver.firstName} lastName={r.driver.lastName} size={52} />
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold" data-person>{r.driver.firstName} {r.driver.lastName}</p>
                    {r.vehicle && (
                      <p className="text-sm text-[var(--text-muted)] truncate">
                        {r.vehicle.make} {r.vehicle.model} · {r.vehicle.color} · <span className="font-mono font-bold text-[var(--text)]">{r.vehicle.plate}</span>
                      </p>
                    )}
                  </div>
                  {r.conversationId && (
                    <Link to={`/app/chat/${r.conversationId}`} className="tap size-12 rounded-full bg-[var(--surface-2)] flex items-center justify-center" aria-label="Chat">
                      <MessageCircle className="size-5" />
                    </Link>
                  )}
                  {r.driver.phone && (
                    <a href={`tel:${r.driver.phone}`} className="tap size-12 rounded-full bg-brand-black text-white dark:bg-white dark:text-brand-black flex items-center justify-center" aria-label="Llamar">
                      <Phone className="size-5" />
                    </a>
                  )}
                </div>
                {!['COMPLETED', 'CANCELLED', 'EXPIRED'].includes(r.status) && (
                  <div className="mt-4 flex flex-col gap-2">
                    <Button variant="secondary" block icon={<Share2 className="size-5" />} loading={sharing} onClick={shareTrip}>
                      Compartir viaje en vivo
                    </Button>
                    <DestinationChangeCustomer ride={r} />
                  </div>
                )}
              </Card>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
